import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useCurrentOrganization } from './use-current-organization';
import { useTranslation } from 'react-i18next';

export interface Priority {
  id: string;
  name: string;
  displayName?: string;
  color?: string;
  isDefault?: boolean;
  organizationId: string;
  createdAt: string;
}

interface UsePrioritiesOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch priorities for the current organization
 * Uses same query key as useOrganizationMetadata to share cache
 * Note: Returns the data directly (Priority[]) for backward compatibility
 */
export function usePriorities(options?: UsePrioritiesOptions) {
  const { organizationId } = useCurrentOrganization();
  const isEnabled = options?.enabled ?? true;
  
  return useQuery<Priority[]>({
    queryKey: ["/api/priorities", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/priorities?organizationId=${encodeURIComponent(organizationId)}` : "/api/priorities";
      const response = await apiRequest(url);
      const result = await response.json();
      return result?.data || [];
    },
    // Always enabled - let the query run even without organizationId (backend will use user's org from session)
    // This prevents race conditions where modal opens before organizationId is available
    enabled: isEnabled,
    staleTime: 30 * 60 * 1000, // 30 minutes - matches useOrganizationMetadata, priorities rarely change
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
    // Allow refetch on mount when data is stale to ensure priorities are available
    refetchOnMount: true,
  });
}

/**
 * Hook to get priority display information (name, color, localized name)
 */
export function usePriorityDisplay(priorityId: string | null | undefined) {
  const { data: priorities = [] } = usePriorities();
  
  const priority = priorityId ? priorities.find(p => p.id === priorityId) : null;
  
  const getDisplayName = (): string => {
    if (!priority) return '-';
    
    // Use displayName from API (database-backed translation) with fallback to name
    return priority.displayName || priority.name;
  };
  
  const getColor = (): string => {
    return priority?.color || '#6b7280';
  };
  
  return {
    priority,
    displayName: getDisplayName(),
    color: getColor(),
    isLoading: !priorities.length && !!priorityId,
  };
}

